import Fuse from "fuse.js";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  closeModal,
  controlToastState,
  Footer,
  openModal,
} from "risa-oasis-ui_v2";
import {
  editCoverMyMedsRequest,
  sendtoPlanRequest,
} from "../../api/bigQuery/nycbsPharmaOrders";
import { addCmmEvent } from "../../api/firebase/firestoreService";
import FormSections from "../../components/formSections/formSections";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import DrugLabelModal from "../../components/modals/drugLabelModal/drugLabelModal";
import SendToPlanConfirmModal from "../../components/modals/sendToPlanConfirmModal/sendToPlanConfirmModal";
import { usePharmaFormFields } from "../../context/pharmaFormFieldsContext";
import { mapToCoverMyMedsInputModel } from "../../data-model/cmmInputRequestModel";
import { OrderStatus } from "../../enums/authStatus";
import { CmmEvents } from "../../enums/cmmEvents";
import { NycbsPharmaOrderKeys } from "../../enums/nycbsPharmaOrder";
import { ScreenNames } from "../../enums/screenNames";
import { fetchSingleOrderDetailsForCmm } from "../../redux/slice/cmm/cmmSingleOrderSlice";
import { fetchAllDiagnosisDetailsForAnOrder } from "../../redux/slice/cmm/dignosisDetailsSlice";
import { fetchAllQuestionFromBigQuery } from "../../redux/slice/cmm/pharmaQuestionsSlice";
import { fetchPrescriptionData } from "../../redux/slice/cmm/prescriptionSlice";
import { fetchSingleOrderDocs } from "../../redux/slice/nycbsPharmaExternal/nycbsDocumentsSlice";
import { AppDispatch, RootState } from "../../redux/store/store";
import BackArrowBlue from "../../svg/back-arrow-blue";
import { CopyIcon } from "../../svg/copy-icon";
import { logDataToConsole } from "../../utils/customLogger";
import { generateFullName } from "../../utils/generateOrderDataForTable";
import { capitalizeString } from "../../utils/stringModifications";
import PharmaPaForm from "./components/pharmaPaForm";
import { getUpdatedFormDataWithFilledValues } from "./utils/getUpdatedFormDataWithFilledValues";

const PharmaPaFormLayout = () => {
  const {
    isAllFieldsValidationFree,
    setShouldRefetchData,
    formFieldsData,
    isFormDirty,
  } = usePharmaFormFields();
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [currentFormId, setCurrentFormId] = useState<string | null>(null);
  const [fetchModalOpen, setFetchModalOpen] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const { data, loading } = useSelector(
    (state: RootState) => state.cmmSingleOrder,
  );

  const { data: formOptions, loading: formOptionsLoading } = useSelector(
    (state: RootState) => state.formOptions,
  );

  const { data: formConfig, loading: formConfigLoading } = useSelector(
    (state: RootState) => state.cmmFormConfig,
  );

  const { data: pharmaQuestionaire, loading: pharmaQuestionaireLoading } =
    useSelector((state: RootState) => state.pharmaQuestions);

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { loading: diagnosisDetailsLoading } = useSelector(
    (state: RootState) => state.diagnosisDetails,
  );

  const { loading: singleOrderDocsLoading } = useSelector(
    (state: RootState) => state.nycbsDocuments,
  );

  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const { id } = useParams();

  const navigate = useNavigate();

  const shouldDisableSendToPlan = () => {
    if (
      data?.[NycbsPharmaOrderKeys.CmmResultKey] === undefined ||
      data?.[NycbsPharmaOrderKeys.CmmResultKey] === null ||
      data?.[NycbsPharmaOrderKeys.CmmResultKey]?.trim() === ""
    ) {
      return true;
    }

    if (!isAllFieldsValidationFree) return true;

    if (isFormDirty) return true;

    return false;
  };

  const onFormSubmit = async (sendToPlan: boolean) => {
    const updatedFormDataWithFilledValues = mapToCoverMyMedsInputModel(
      getUpdatedFormDataWithFilledValues(formFieldsData),
    );
    logDataToConsole("Final CMM Data", {
      cover_my_meds_input: updatedFormDataWithFilledValues,
      send_to_plan: false,
    });

    if (sendToPlan && pharmaQuestionaire === null) {
      sendtoPlanRequest(
        updatedFormDataWithFilledValues,
        id ?? "",
        ScreenNames.PHARMA_PA_FORM,
        user?.email ?? "",
      );
      controlToastState("send-to-plan-success");
    } else {
      editCoverMyMedsRequest(
        {
          cover_my_meds_input: updatedFormDataWithFilledValues,
          send_to_plan: false,
        },
        id ?? "",
        ScreenNames.PHARMA_PA_FORM,
        user?.email ?? "",
      );
      controlToastState("form-save-success");
    }
    if (pharmaQuestionaire) {
      navigate(`/pharma-pa-worklists/pharma-pa-questionaire/${id}`);
    } else {
      navigate(`/pharma-pa-worklists/`);
    }
  };

  const getFormConfigId = (planName: string, formConfiguration) => {
    if (!planName || planName === "") {
      return "default";
    }

    const fuzzySearchOptions = {
      includeScore: true,
      threshold: 0.3,
      distance: 200,
      useExtendedSearch: true,
      keys: ["id"],
    };

    const options = fuzzySearchOptions;
    const fuse = new Fuse(formConfiguration, options);
    const result = fuse.search(planName);
    const mostMatchedSearch = result?.[0]?.item;

    return mostMatchedSearch?.["id"] ?? "default";
  };

  useEffect(() => {
    if (formConfig && data) {
      const planNameFromDb = getFormConfigId(
        data?.[NycbsPharmaOrderKeys.PlanName] ?? "",
        formConfig,
      );
      setCurrentFormId(planNameFromDb);
    }
  }, [formConfig, data]);

  useEffect(() => {
    if (id) {
      dispatch(fetchSingleOrderDetailsForCmm(id));
      dispatch(fetchAllQuestionFromBigQuery(id));
      dispatch(fetchPrescriptionData(id));
      dispatch(fetchAllDiagnosisDetailsForAnOrder(id));
      dispatch(fetchSingleOrderDocs(id));
    }
  }, [id]);

  useEffect(() => {
    openConfirmModal && openModal("send-to-plan-confirm-modal");
  }, [openConfirmModal]);

  useEffect(() => {
    if (fetchModalOpen) {
      openModal("drug-label-modal");
    }
  }, [fetchModalOpen]);

  return (
    <>
      {loading ||
      formOptionsLoading ||
      formConfigLoading ||
      pharmaQuestionaireLoading ||
      diagnosisDetailsLoading ||
      singleOrderDocsLoading ? (
        <div className="full-screen__loader flex h-full flex-col">
          <LoaderMessage message="Fetching form information..." />
        </div>
      ) : (
        formOptions &&
        formOptions.length > 0 && (
          <div className="pharma-pa-form__container flex h-full flex-col bg-[#F7F9FA] px-[0.9375rem] py-[0.625rem]">
            <div className="pharma-pa-form__header mb-4">
              <div
                className="pharma-pa-form__header header-content flex items-center gap-1 text-h12 font-bold text-tertiaryBlue-4 hover:cursor-pointer"
                onClick={() => {
                  setShouldRefetchData(true);
                  setTimeout(() => {
                    navigate(`/pharma-pa-worklists/insurance-details/${id}`);
                  }, 0);
                }}
              >
                <BackArrowBlue />
                Go Back
              </div>
            </div>
            <div className="pharma-pa-form__body flex flex-1 flex-col overflow-hidden rounded bg-white p-4 pb-0">
              <div className="pharma-pa-form__body--header mb-4">
                <div className="pharma-pa-form__body--header-details flex items-center gap-2">
                  <div className="pharma-pa-form__body page-name text-large font-semiBold leading-6 text-black">
                    Prior Authorisation Form
                  </div>
                  <div className="insurance-details__body-patient-name rounded-[1.875rem] bg-primaryGray-16 px-4 text-small font-semibold leading-6 text-primaryGray-1">
                    {capitalizeString(
                      generateFullName(
                        data?.[NycbsPharmaOrderKeys.PatientFirstName] || "",
                        "",
                        data?.[NycbsPharmaOrderKeys.PatientLastName] || "",
                      ),
                    )}
                    &nbsp;&nbsp;<span>&#8226;</span>&nbsp;&nbsp;
                    {data?.[NycbsPharmaOrderKeys.PatientMrn] ?? ""}
                    &nbsp;&nbsp;<span>&#8226;</span>&nbsp;&nbsp;
                    {data?.[NycbsPharmaOrderKeys.PatientDob] ?? ""}
                    &nbsp;&nbsp;<span>&#8226;</span>&nbsp;&nbsp;
                    {data?.[NycbsPharmaOrderKeys.DrugName]?.split(" ")[0] ?? ""}
                  </div>
                  <div className="rounded-[1.875rem] bg-primaryGray-16 px-4 text-small font-semibold leading-6 text-primaryGray-1">
                    {data?.type ?? ""}
                  </div>

                  <div className="cmm-result-key ml-auto flex items-center gap-2 text-small">
                    Cmm Key:{" "}
                    <span className="font-bold">
                      {data?.[NycbsPharmaOrderKeys.CmmResultKey] ?? ""}&nbsp;
                    </span>
                    <div
                      className="cmm-result-key copy-icon mr-2 hover:cursor-pointer"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          data?.[NycbsPharmaOrderKeys.CmmResultKey] ?? "",
                        );
                      }}
                    >
                      <CopyIcon />
                    </div>
                    <Button
                      buttonType="secondary"
                      size="small"
                      disabled={false}
                      onClick={() => {
                        setFetchModalOpen(true);
                      }}
                    >
                      Fetch Drug Usage
                    </Button>
                  </div>
                </div>
              </div>
              <div className="pharma-pa-form__body content flex flex-1 overflow-hidden">
                <div className="pharma-pa-form__body form-sections w-1/5">
                  <FormSections
                    activeSection={
                      activeSectionId ? `section-${activeSectionId}` : undefined
                    }
                    formConfiguration={formConfig ?? []}
                    matchedKey={currentFormId ?? ""}
                  />
                </div>
                <div className="pharma-pa-form__body main-form h-full w-4/5 overflow-auto overflow-x-hidden border border-primaryGray-16 p-4">
                  <PharmaPaForm
                    sectionOnClick={(value) => {
                      setActiveSectionId(value as string);
                    }}
                    docId={id ?? ""}
                  />
                </div>
              </div>
            </div>
            <div className="pharma-pa-form__footer">
              <Footer
                cancelButtonText={"Save"}
                submitButtonText={
                  pharmaQuestionaire === null ? "Send To Plan" : "Continue"
                }
                onCancelClick={() => {
                  addCmmEvent(id ?? "", {
                    event: CmmEvents.SAVE_CLICKED_ON_FORM,
                    screen_name: ScreenNames.PHARMA_PA_FORM,
                    created_at: new Date(),
                    email: user?.email ?? "",
                  });
                  onFormSubmit(false);
                }}
                onSubmitClick={() => {
                  if (pharmaQuestionaire === null) {
                    setOpenConfirmModal(true);
                    addCmmEvent(id ?? "", {
                      event: CmmEvents.SEND_TO_PLAN_CLICKED,
                      screen_name: ScreenNames.PHARMA_PA_FORM,
                      created_at: new Date(),
                      email: user?.email ?? "",
                    });
                  } else {
                    addCmmEvent(id ?? "", {
                      event: CmmEvents.CONTINUE_CLICKED_ON_FORM,
                      screen_name: ScreenNames.PHARMA_PA_FORM,
                      created_at: new Date(),
                      email: user?.email ?? "",
                    });
                    navigate(
                      `/pharma-pa-worklists/pharma-pa-questionaire/${id}`,
                    );
                  }
                }}
                isDisabled={shouldDisableSendToPlan()}
                isCancelDisabled={
                  !isFormDirty ||
                  data?.[NycbsPharmaOrderKeys.Status] ===
                    OrderStatus.SENT_TO_PLAN
                }
              />
              {openConfirmModal && (
                <SendToPlanConfirmModal
                  onClose={() => setOpenConfirmModal(false)}
                  onSaveClick={() => {
                    addCmmEvent(id ?? "", {
                      event: CmmEvents.SEND_TO_PLAN_MODAL_CONFIRM,
                      screen_name: ScreenNames.SEND_TO_PLAN_MODAL,
                      created_at: new Date(),
                      email: user?.email ?? "",
                    });
                    onFormSubmit(true);
                  }}
                />
              )}
              {fetchModalOpen && (
                <DrugLabelModal
                  onClose={() => {
                    setFetchModalOpen(false);
                    closeModal("drug-label-modal");
                  }}
                />
              )}
            </div>
          </div>
        )
      )}
    </>
  );
};

export default PharmaPaFormLayout;
